import { useEffect } from 'react';
import { useLocalStorage } from 'usehooks-ts';

import * as graphql from '@treams/treams-network';

import { useSupportedLanguageIDs } from './useSupportedLanguageIDs';

type Language = {
	id: string;
	loading: boolean;
};

const DEFAULT_LOCALE = 'nl_NL';

function useBestEffortLocale(): { locale: string; loading: boolean } {
	const browserLocale = navigator.language.replace('-', '_');

	const { languageIDs, loading } = useSupportedLanguageIDs();
	return {
		locale: languageIDs.find((locale) => locale === browserLocale) ?? DEFAULT_LOCALE,
		loading,
	};
}

export function useLanguage(): Language {
	const viewer = graphql.useViewerQuery();

	const { locale: bestEffortLocale, loading } = useBestEffortLocale();

	// 1. Initial value is the "best effort locale" OR the default locale.
	// 2. If this is already set, use local storage stored value.
	const [localStorageLanguage, setLocalStorageLanguage] = useLocalStorage(
		'locale',
		bestEffortLocale ?? DEFAULT_LOCALE,
	);

	useEffect(() => {
		if (viewer.data?.viewer?.locale) {
			setLocalStorageLanguage(viewer.data.viewer.locale);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [viewer.data?.viewer?.locale]);

	return {
		// 3. Prefer current viewer locale from server, then local storage.
		id: viewer.data?.viewer?.locale ?? localStorageLanguage,
		loading,
	};
}
